export const PROJECT_STAGE_STORY = {
  discoveryDesign: {
    chapterNarrative:
      "This is where the drum begins long before wood is cut, hardware is chosen, or a finish is imagined. The first responsibility is not to build a shell — it is to understand the player. I want to know what they hear in their head that they have not yet been able to find in their hands. I want to know what has been missing, what keeps disappointing them, what inspires them, and what kind of sound feels like home. This chapter is about listening closely enough that the instrument starts revealing itself before I ever touch a tool. Every great build begins with honesty, clarity, and a real understanding of the drummer’s journey.",

    artistIntent:
      "The goal here is to understand the player at a level deeper than specs. Not just diameter, depth, wood, or finish — but identity, frustration, aspiration, and voice. What are they chasing? What have they outgrown? What do they need this drum to say that their current instrument does not?",

    artistInfluences: [
      "The player’s musical history",
      "The records and drummers that shaped their ear",
      "What they love in theory versus what they actually need in practice",
      "What feels missing in their current setup",
    ],

    artistEmotionalTargets: [
      "Trust",
      "Clarity",
      "Excitement",
      "A sense of being understood",
    ],

    artistUseCases: [
      "Studio precision",
      "Stage authority",
      "Daily inspiration",
      "A deeply personal flagship instrument",
    ],

    listeningFor:
      "I listen for the difference between what a player says they want and what they really mean. Sometimes they ask for sensitivity but what they are really after is response. Sometimes they ask for body but what they really mean is authority. This chapter is about translating language into truth.",

    craftsmanInterpretation:
      "My job in this chapter is to separate trend from truth. I am not trying to impress the player with options. I am trying to understand what should be permanent and what should be left out. The build gets stronger when the vision gets cleaner.",

    materialStrategy:
      "At this stage, material choices remain open, but they are already being narrowed by purpose. I start weighing shell behavior, density, articulation, warmth, projection, and feel against the drummer’s needs. The goal is not to chase luxury for its own sake — it is to choose a path that will eventually feel inevitable.",

    standardFoundations: [
      "Deep consultation before commitment",
      "Clear translation of musical goals into physical design",
      "No guessing and no trend-chasing",
      "Every future decision must serve the player, not the catalog",
    ],

    craftsmanChoices: [
      "Identifying what kind of shell character will best support the player",
      "Determining whether the build should lean expressive, controlled, dry, open, heavy, or balanced",
      "Defining what details are essential versus distracting",
      "Beginning the emotional and sonic blueprint for the drum",
    ],

    voiceSummary:
      "This chapter affects everything because it defines the reason the instrument will exist at all. If this part is shallow, the rest becomes decoration. If this part is honest, every later choice gains weight, intention, and legitimacy.",

    voiceEffects: [
      "Identity",
      "Direction",
      "Trust in the process",
      "The emotional center of the instrument",
    ],

    voiceMantra:
      "Before the shell can speak clearly, the builder has to listen clearly.",
  },

  commitmentPortal: {
    chapterNarrative:
      "This chapter is where vision stops being hypothetical and starts becoming binding. It is the moment the build crosses from possibility into commitment. The consultation becomes structure. The broad emotional picture becomes a real design language. Dimensions, construction approach, feature priorities, and expectations begin tightening into a path that can actually be built. This stage matters because once the build is committed, every choice after this needs to move with confidence, not hesitation.",

    artistIntent:
      "The player needs to feel that the instrument now has direction. This chapter is about making sure they are not just excited — they are aligned. The commitment should feel intentional, not rushed. It should feel like the right drum is being chosen for the right reasons.",

    artistInfluences: [
      "The consultation conversation and emotional takeaways",
      "What the player wants the drum to solve in their setup",
      "What they respond to visually versus what they respond to sonically",
      "What kind of ownership experience they want this build to carry",
    ],

    artistEmotionalTargets: [
      "Confidence",
      "Commitment",
      "Momentum",
      "A sense of investment in something real",
    ],

    artistUseCases: [
      "Approving the build path",
      "Locking in the overall direction",
      "Beginning the personal relationship with the instrument",
      "Moving from dream to execution",
    ],

    listeningFor:
      "At this stage I listen for hesitation. If something still feels vague, forced, or compromised, it needs to be addressed now. I want the player to feel conviction, because any uncertainty at this stage tends to echo through the rest of the build.",

    craftsmanInterpretation:
      "This is where I start translating conversation into real-world tolerances. Romantic ideas need to survive contact with physical reality. My job is to preserve the soul of the concept while making sure the design will actually perform, hold up, and justify its existence once it is in the player’s hands.",

    materialStrategy:
      "Now the strategy becomes more specific. Shell structure, depth, diameter, wood behavior, reinforcement philosophy, hardware weight, and tuning goals begin to form a connected system. I am no longer just exploring what could work — I am selecting what should work together.",

    standardFoundations: [
      "No build direction gets locked without purpose",
      "Aesthetic choices must still support the instrument’s identity",
      "Performance always outranks novelty",
      "The player’s real needs stay at the center of the decision-making",
    ],

    craftsmanChoices: [
      "Locking in the shell philosophy",
      "Defining the relationship between sound, response, and feel",
      "Narrowing feature decisions into a coherent build language",
      "Eliminating options that dilute the core vision",
    ],

    voiceSummary:
      "This chapter affects cohesion. It determines whether the instrument will feel like one complete statement or a collection of disconnected ideas. When this stage is done right, the rest of the build moves with much more force and less friction.",

    voiceEffects: [
      "Cohesion",
      "Direction",
      "Confidence in execution",
      "Structural honesty",
    ],

    voiceMantra:
      "A great custom drum is not built from endless options. It is built from the right commitments.",
  },

  woodVisionLockIn: {
    chapterNarrative:
      "This is where the build starts becoming tactile. The vision is no longer floating in conversation alone — it begins attaching itself to actual material. Wood selection is not just about beauty. It is about behavior, tension, grain integrity, density, movement, and what kind of voice the shell wants to produce under pressure. This chapter is about matching the physical truth of the material to the emotional truth of the player. If that marriage is wrong, the build will always feel slightly dishonest. If it is right, the instrument starts carrying its own gravity.",

    artistIntent:
      "The player may experience this chapter through visuals first, but what matters most is that the chosen material feels like the correct foundation for their sound and story. This is where the instrument starts becoming undeniably theirs.",

    artistInfluences: [
      "Desired balance of articulation, warmth, and projection",
      "How alive or controlled the drum should feel",
      "Visual character that reflects the player’s identity",
      "How the shell should behave under different dynamic ranges",
    ],

    artistEmotionalTargets: [
      "Connection",
      "Anticipation",
      "Recognition",
      "The sense that the drum is becoming real",
    ],

    artistUseCases: [
      "Confirming the shell’s emotional and sonic foundation",
      "Aligning aesthetics with performance goals",
      "Creating a stronger sense of ownership in the build",
      "Choosing material that feels worthy of the story behind it",
    ],

    listeningFor:
      "I listen for whether the material agrees with the goal. I ask myself whether this wood will fight the desired response or support it. I think about attack shape, note length, tonal center, and how the shell will feel when pushed. The right wood should not merely look beautiful — it should make the intended voice easier to achieve.",

    craftsmanInterpretation:
      "This is where material stops being decorative and starts being decisive. I study grain consistency, structural reliability, visual flow, and the subtle cues that tell me how the shell may behave later. I want the wood to feel chosen, not assigned. Every piece has a temperament. The job is to match that temperament to the drummer.",

    materialStrategy:
      "Here the selection becomes highly intentional. I weigh density, grain orientation, hardness, stability, and visual character against what the player needs from the final voice. The shell foundation should support tuning ease, tonal identity, and long-term reliability — not just first impressions.",

    standardFoundations: [
      "Material must earn its place functionally and aesthetically",
      "Shell wood is chosen for behavior, not just appearance",
      "The visual character should reinforce the instrument’s identity",
      "Every selection must support the long arc of the build",
    ],

    craftsmanChoices: [
      "Selecting wood with the right sonic behavior and structural integrity",
      "Evaluating grain and material temperament for the intended shell design",
      "Balancing visual drama with practical performance",
      "Making sure the foundation feels personal, not generic",
    ],

    voiceSummary:
      "This chapter affects the core personality of the shell. It shapes how the instrument will speak, how it will react, how it will tune, and how believable it will feel as a custom piece. The wrong material can force compromise. The right material makes the rest of the build more honest.",

    voiceEffects: [
      "Shell personality",
      "Response",
      "Tonal center",
      "Physical and emotional authenticity",
    ],

voiceMantra:
  "The shell does not become personal when the badge goes on. It becomes personal when the right material is chosen for the right player.",
  },

  rawShellCreation: {
    chapterNarrative:
      "This is where the build stops being theoretical and starts earning its body. Raw shell creation is one of the first moments where the instrument begins to carry mass, geometry, and consequence. This stage is about converting selected material into something structurally honest — something that already contains the first signs of the drum’s eventual voice. I am looking for more than successful assembly. I am looking for alignment, integrity, tension, and the feeling that the shell is beginning to hold itself with authority. This chapter sets the physical tone for everything that follows.",

    artistIntent:
      "From the player’s perspective, this is the moment the build begins to feel undeniably real. The instrument is no longer just a plan or a set of decisions. It is becoming an object with presence. The goal is for the player to feel that the foundation is being built with seriousness, discipline, and zero shortcuts.",

    artistInfluences: [
      "How immediate or controlled the shell should feel",
      "Whether the drum should lean more explosive or more composed",
      "How much body, density, and authority the player wants under the stick",
      "The kind of confidence the shell should project before any finish or hardware is added",
    ],

    artistEmotionalTargets: [
      "Momentum",
      "Trust in the structure",
      "Excitement",
      "A sense of real emergence",
    ],

    artistUseCases: [
      "Establishing the shell as the instrument’s structural center",
      "Beginning the transition from concept to physical identity",
      "Laying the groundwork for projection, response, and tuning behavior",
      "Making sure the build has real backbone before refinement begins",
    ],

    listeningFor:
      "At this stage I am listening with my hands as much as my ears. I pay attention to resistance, alignment, stability, and the way the shell begins to behave as a single form. I ask whether it already feels trustworthy. I ask whether the structure is giving me the kind of confidence that a serious instrument should give before it is ever finished.",

    craftsmanInterpretation:
      "This chapter is about discipline. Precision matters here because every small inaccuracy gets amplified later. I am watching how the shell comes together, how consistent the geometry feels, whether the joins feel confident, and whether the build is developing the kind of integrity that allows refinement to mean something. A shell should never need finish or hardware to feel convincing.",

    materialStrategy:
      "Now the chosen material starts proving itself. I am thinking about how the shell should hold shape, how the structure will react under tension, and how the build method will influence the eventual voice. The goal is to create a shell that already feels intentional before the finer performance details are introduced.",

    standardFoundations: [
      "Structural integrity before visual refinement",
      "Tight tolerances and clean geometry",
      "No compensation for preventable inaccuracies later",
      "The shell must feel trustworthy before it is made beautiful",
    ],

    craftsmanChoices: [
      "Guiding the shell into stable and accurate form",
      "Watching for any material behavior that could affect long-term performance",
      "Making sure the structure reflects the intended personality of the build",
      "Establishing the physical seriousness of the instrument early",
    ],

    voiceSummary:
      "This chapter affects the authority of the shell itself. It contributes to how solid the drum feels, how confidently it projects, and how much credibility it carries before any of the more obvious refinement stages arrive. A strong shell gives later decisions something real to stand on.",

    voiceEffects: [
      "Structural authority",
      "Shell confidence",
      "Projection potential",
      "The first physical signs of the drum’s character",
    ],

    voiceMantra:
      "Before a drum can become expressive, it has to become structurally undeniable.",
  },

  shellTrueingTorchTune: {
    chapterNarrative:
      "This chapter is where discipline meets sensitivity. The shell may already exist, but now it has to be corrected, refined, and brought into honest alignment. Trueing is not glamorous work, but it is sacred work. This is where I pay attention to the shell’s willingness to cooperate. I am looking for accuracy, consistency, and the subtle signs that the shell is starting to respond like an instrument instead of just a structure. Small variations matter here. Tiny errors in geometry can become major frustrations in tuning, feel, and response. This is the chapter where the shell learns how to behave.",

    artistIntent:
      "The player may never directly see the importance of this stage in the same way they notice finish or hardware, but they will absolutely feel it later. The goal is to give them a drum that tunes with less fighting, responds more honestly, and carries a sense of ease that cannot be faked.",

    artistInfluences: [
      "Need for reliable tuning behavior",
      "How open or focused the response should feel",
      "Whether the player values headroom, control, or ease under different tunings",
      "The desire for an instrument that feels cooperative instead of stubborn",
    ],

    artistEmotionalTargets: [
      "Confidence",
      "Control",
      "Relief from inconsistency",
      "The sense that the drum wants to work with them",
    ],

    artistUseCases: [
      "Improving shell truth and tuning stability",
      "Setting the stage for more predictable response",
      "Reducing friction between player and instrument",
      "Preparing the drum for sensitive edge and bed work later",
    ],

    listeningFor:
      "I listen for whether the shell wants to settle into correctness. I watch for wobble, resistance, imbalance, and subtle irregularities that might later create tuning frustration. I ask whether the shell is beginning to feel centered. This chapter is full of questions like: Does it want to tune cleanly? Does it want to sit evenly? Does it feel calm under scrutiny?",

    craftsmanInterpretation:
      "This is one of the most revealing chapters in the build because it exposes whether the shell truly deserves refinement. I am checking geometry, surface truth, consistency, and the shell’s overall readiness for performance-critical work. If I rush this stage, the drum pays for it later. If I get it right, the instrument gains a kind of quiet authority that the player will feel every time they tune it.",

    materialStrategy:
      "The strategy here is restraint, patience, and correction without overworking. The shell must be brought into dependable truth while preserving its integrity. I want accuracy, but I do not want to strip the shell of life. The goal is not sterile perfection — it is trustworthy performance.",

    standardFoundations: [
      "True shell geometry matters more than speed",
      "Small inaccuracies are treated seriously",
      "The shell must be prepared for ease, not excuses",
      "Precision here protects every later tuning decision",
    ],

    craftsmanChoices: [
      "Refining shell truth for dependable performance",
      "Eliminating subtle inconsistencies before edge work begins",
      "Reading the shell’s behavior instead of forcing it blindly",
      "Protecting the player from future frustration through present discipline",
    ],

    voiceSummary:
      "This chapter affects tuning ease, seating consistency, and the shell’s willingness to respond evenly. It is one of the hidden chapters that separates a drum that merely works from a drum that feels unusually cooperative and mature.",

    voiceEffects: [
      "Tuning behavior",
      "Shell consistency",
      "Responsiveness",
      "Player confidence",
    ],

    voiceMantra:
      "A shell that is not honest in its geometry will never be fully honest in its voice.",
  },

  exteriorArtFinish: {
    chapterNarrative:
      "This is where the instrument begins to show its face without losing its soul. Exterior art and finish are not cosmetic afterthoughts — they are the visible expression of what the build has already become. The challenge here is to create something that feels worthy of the player and worthy of the drum without crossing into vanity. The finish should deepen the instrument’s identity, not distract from it. I want the drum to look like itself, not like it is trying too hard. This chapter is about restraint, taste, and making sure beauty still answers to purpose.",

    artistIntent:
      "The player should see themselves in the instrument here. Not just in a literal visual sense, but in the way the finish carries attitude, seriousness, mood, and identity. This is where the build starts becoming unmistakably personal on first sight.",

    artistInfluences: [
      "The player’s taste and visual identity",
      "Whether the build should feel elegant, dangerous, warm, raw, refined, or bold",
      "How much visual drama supports the instrument versus overwhelms it",
      "How the drum should feel when first pulled from the case or placed under lights",
    ],

    artistEmotionalTargets: [
      "Recognition",
      "Pride",
      "Connection",
      "The feeling that the drum already belongs to them",
    ],

    artistUseCases: [
      "Giving the build visual identity that matches its voice",
      "Translating consultation tone into finish language",
      "Balancing statement and restraint",
      "Making the instrument look as intentional as it sounds",
    ],

    listeningFor:
      "I listen for visual honesty. Does this finish deepen the story, or does it compete with it? Does it make the drum feel more like itself, or less? I think about the player opening the case years from now and whether the finish will still feel true rather than trendy.",

    craftsmanInterpretation:
      "This chapter is about knowing when to push and when to stop. Finish can elevate a build, but it can also bury it. I am watching how light moves across the shell, how texture and depth behave, and whether the visual decisions still feel anchored in the same truth established during consultation. The right finish should feel inevitable.",

    materialStrategy:
      "Here the strategy is to preserve and reveal rather than simply coat. The shell’s surface character, movement, grain, and attitude need to remain alive. The finish should support durability and beauty, but it also needs to protect the emotional integrity of the piece.",

    standardFoundations: [
      "Beauty must still answer to identity",
      "Finish should support the drum, not overshadow it",
      "Visual decisions must age well",
      "Restraint is part of craftsmanship",
    ],

    craftsmanChoices: [
      "Choosing a visual language that matches the shell’s voice",
      "Refining color, depth, sheen, and mood with intention",
      "Making sure the finish reflects the player without becoming costume",
      "Preserving the shell’s honesty while elevating its presence",
    ],

    voiceSummary:
      "This chapter affects first impression, emotional connection, and the drum’s visible personality. While it does not change the shell’s core acoustics the way edges or structure do, it absolutely changes the way the instrument is felt, valued, and remembered.",

    voiceEffects: [
      "Visual identity",
      "Emotional ownership",
      "Presence",
      "The visible seriousness of the build",
    ],

    voiceMantra:
      "A finish should not mask the truth of a drum. It should reveal it with authority.",
  },

  edgesSnareBeds: {
    chapterNarrative:
      "This is one of the most decisive chapters in the entire build. Bearing edges and snare beds are where the shell stops being an abstract possibility and starts becoming a true performance surface. These details are not decorative refinements — they are the language through which the drum speaks to the head, the wires, and the player. This chapter demands patience because small differences here change everything. Sensitivity, body, sustain, articulation, tuning ease, snare response, and overall feel are all on the line. This is the chapter where geometry becomes musical.",

    artistIntent:
      "The player may have described what they want in emotional or practical language during consultation, but this is where many of those requests finally become physical. If they wanted ease, sensitivity, precision, throat, depth, articulation, or authority, this is one of the places where those needs get carved into reality.",

    artistInfluences: [
      "Desired tuning range and tolerance",
      "Need for sensitivity and snare articulation",
      "How immediate or forgiving the stick response should feel",
      "Whether the drum should lean more open, controlled, dry, or elastic",
    ],

    artistEmotionalTargets: [
      "Precision",
      "Responsiveness",
      "Ease",
      "The feeling that the drum instantly gives something back",
    ],

    artistUseCases: [
      "Maximizing tuning ease and head contact",
      "Shaping how the shell speaks under the stick",
      "Dialing in wire response and snare sensitivity",
      "Turning consultation goals into undeniable playability",
    ],

    listeningFor:
      "I ask myself whether the edge profile truly supports the player’s needs or just follows convention. I think about how the head will seat, how the drum will breathe, and whether the snare side will reward nuance without becoming fragile or temperamental. I listen for the future voice here long before a full setup is installed.",

    craftsmanInterpretation:
      "This is detail work with major consequences. The shell has to be read carefully. I am shaping surfaces that the player may never consciously inspect, yet they will feel their influence in every tuning adjustment and every stroke. I want the edge work to disappear in the best way possible — meaning it does its job so well that the player simply experiences ease, articulation, and trust.",

    materialStrategy:
      "The strategy here is to shape contact and release with intention. Every cut needs to respect the shell’s structure, the desired voice, and the way the drum is meant to live across tension ranges. The right geometry creates cooperation between shell, head, and wires. That relationship is everything.",

    standardFoundations: [
      "Edge work must match the instrument’s intended voice",
      "Snare beds must support articulation without compromising stability",
      "Tiny decisions here create major downstream results",
      "Playability is treated as sacred, not secondary",
    ],

    craftsmanChoices: [
      "Defining the shell-to-head relationship with precision",
      "Shaping response, sensitivity, and tuning behavior through geometry",
      "Balancing openness with control",
      "Making sure the drum feels intelligent under the player’s hands",
    ],

    voiceSummary:
      "This chapter has one of the strongest impacts on how the drum actually plays. It affects tuning ease, snare response, articulation, feel under the stick, note shape, and the overall sense of whether the instrument is working with the player or against them.",

    voiceEffects: [
      "Tuning ease",
      "Sensitivity",
      "Snare articulation",
      "Stick response",
    ],

    voiceMantra:
      "This is where craftsmanship stops being theory and starts becoming feel.",
  },

  hardwareAssembly: {
    chapterNarrative:
      "This chapter is where the instrument is asked to carry itself fully for the first time. Hardware assembly is not just a process of attaching parts — it is where pressure, fit, balance, and mechanical honesty get tested in real terms. The shell now has to hold its visual identity, structural accuracy, and sonic intention under actual working conditions. I want every component to feel like it belongs there, not just physically but philosophically. Nothing should feel ornamental for its own sake. This chapter is about turning the shell into a functioning instrument without diluting what made it special in the first place.",

    artistIntent:
      "The player wants the drum to feel complete, dependable, and serious. They want it to look cohesive, but they also want it to perform without excuses. This chapter is where the drum starts feeling like something that can actually go to work.",

    artistInfluences: [
      "Desired feel under tension",
      "Tolerance for dryness versus openness",
      "Preferred aesthetic weight and personality",
      "Need for long-term reliability and confidence on stage or in studio use",
    ],

    artistEmotionalTargets: [
      "Confidence",
      "Authority",
      "Cohesion",
      "The sense that the build is becoming whole",
    ],

    artistUseCases: [
      "Converting the shell into a full performance instrument",
      "Testing the relationship between structure and component choices",
      "Establishing visual and functional cohesion",
      "Preparing the drum for meaningful tuning and voicing work",
    ],

    listeningFor:
      "I listen for fit, balance, and whether the hardware is helping the shell become more itself or less. I pay attention to how tension is distributed, how solid the assembly feels, and whether anything introduces noise, drag, imbalance, or compromise. This chapter asks a simple question: does the instrument feel complete, or merely assembled?",

    craftsmanInterpretation:
      "This is where execution has to stay clean. Hardware can expose sloppiness quickly. I am watching alignment, consistency, pressure distribution, and the way the full instrument feels in the hands. I want the finished assembly to feel inevitable, like every part arrived exactly where it was always supposed to be.",

    materialStrategy:
      "The strategy now is integration. Component choice, shell personality, and intended voice must all work together under real-world tension. Hardware should support the shell’s behavior, not choke it or disguise it. The best assembly feels coherent before the first serious tuning pass even begins.",

    standardFoundations: [
      "Components must support the shell rather than dominate it",
      "Fit and alignment are treated as performance issues, not cosmetic ones",
      "The full instrument must feel cohesive in hand",
      "Reliability matters as much as visual polish",
    ],

    craftsmanChoices: [
      "Ensuring hardware complements the shell’s voice and feel",
      "Dialing in clean, accurate integration across all mounted components",
      "Protecting the build from mechanical compromise",
      "Making the instrument feel whole, not pieced together",
    ],

    voiceSummary:
      "This chapter affects tuning stability, feel under tension, mechanical confidence, and the overall sense of whether the drum is truly ready to perform. It is where the shell’s vision is asked to survive real assembly.",

    voiceEffects: [
      "Mechanical confidence",
      "Stability",
      "Visual cohesion",
      "The feeling of completeness",
    ],

    voiceMantra:
      "A custom drum should never feel like parts attached to a shell. It should feel like one fully realized statement.",
  },

  legacyTuningMedia: {
    chapterNarrative:
      "This chapter is where the drum is finally asked the hardest question: who are you, really? Tuning and detailing are not cleanup steps — they are where the instrument’s actual voice is confronted, shaped, and revealed. Everything up to this point has been building toward this conversation. This is where I start listening for the truth of the drum, not just the hope of it. I am paying attention to how it speaks at different tensions, how it reacts under light and heavy playing, how the shell opens, how the wires respond, and whether the build is delivering on the promise made back in consultation. This chapter is where the instrument stops being a project and becomes a voice.",

    artistIntent:
      "The player wants confirmation here. They want the drum to prove that the process meant something. They want to hear that the identity discussed at the beginning is now alive in a real, playable instrument.",

    artistInfluences: [
      "Desired note shape and response under different dynamics",
      "How the drum should behave in real musical settings",
      "The balance between immediacy, body, crack, sensitivity, and control",
      "Whether the instrument truly fills the gap that led the player here in the first place",
    ],

    artistEmotionalTargets: [
      "Validation",
      "Excitement",
      "Connection",
      "The feeling that the drum has become what it was meant to be",
    ],

    artistUseCases: [
      "Final voicing and response shaping",
      "Confirming the consultation translated truthfully into sound",
      "Capturing the instrument in media with integrity",
      "Preparing the drum for ownership, legacy, and memory",
    ],

    listeningFor:
      "This is the chapter where I listen hardest. I ask whether the drum speaks with conviction. I ask whether it has enough give, enough bite, enough body, enough honesty. I listen for the thing that made this build necessary in the first place. I am not chasing a generic good sound. I am chasing the right sound for this player.",

    craftsmanInterpretation:
      "My role here is part builder, part editor, part witness. I am making fine decisions, but I am also stepping back and letting the drum tell me what it naturally wants to do. The best tuning chapter is not about forcing the shell into a preconceived idea. It is about recognizing when the shell, the build, and the player’s needs finally lock together.",

    materialStrategy:
      "At this point material strategy becomes response strategy. The shell, edges, components, and overall build are now interacting as one system. The goal is to reveal their strongest alignment and make small adjustments that let the instrument speak with maximum honesty and authority.",

    standardFoundations: [
      "Final voicing must honor the original intent of the build",
      "The shell’s truth must be heard, not overridden",
      "Media should document the drum honestly",
      "This chapter should confirm the build, not rescue it",
    ],

    craftsmanChoices: [
      "Dialing in the tuning range that best reveals the shell’s identity",
      "Listening for where the drum stops resisting and starts speaking freely",
      "Refining response so the player feels immediate connection",
      "Capturing the instrument in a way that respects its real character",
    ],

    voiceSummary:
      "This chapter affects the player’s first true encounter with the completed voice of the instrument. It shapes perception, trust, validation, and whether the build fully lands emotionally and sonically. This is one of the most human chapters in the entire journey.",

    voiceEffects: [
      "Final voice",
      "Player connection",
      "Emotional validation",
      "The documented identity of the drum",
    ],

    voiceMantra:
      "A drum is finished when it can finally tell the truth about why it was built.",
  },

  finalQAPackagingDelivery: {
    chapterNarrative:
      "This final chapter is about respect. Respect for the player, respect for the process, and respect for the instrument that has now earned the right to leave the bench. Quality assurance is not just about catching mistakes — it is about confirming that nothing lazy, loose, or uncertain remains. Packaging and delivery are part of that same philosophy. The build should not only sound complete; it should arrive complete. This chapter is where I make sure the instrument leaves with the same seriousness it was built with. The end of the build should feel intentional, not abrupt.",

    artistIntent:
      "The player wants assurance that what arrives is worthy of the process they committed to. They want to feel that care extends all the way through the final moment — not just until the interesting parts are finished.",

    artistInfluences: [
      "Need for trust at handoff",
      "Expectation of craftsmanship that carries through the final details",
      "Desire for a delivery experience that feels personal and elevated",
      "Confidence that the instrument has been checked, protected, and sent with purpose",
    ],

    artistEmotionalTargets: [
      "Assurance",
      "Pride",
      "Closure",
      "The feeling of receiving something truly finished",
    ],

    artistUseCases: [
      "Final inspection and sign-off",
      "Protecting the drum for transport and first arrival",
      "Making the delivery experience feel consistent with the build journey",
      "Closing the process with care, dignity, and precision",
    ],

    listeningFor:
      "At this stage I am listening for anything unresolved — mechanically, visually, structurally, or emotionally. I ask whether there is any detail that would bother me if this were my own instrument. The final chapter is about refusing to let fatigue make decisions.",

    craftsmanInterpretation:
      "This chapter tests character. It is easy to relax once the exciting work is done, but real craftsmanship carries all the way through to inspection, presentation, and delivery. I want the final handoff to feel as disciplined as the first consultation. The last impression matters because it confirms everything that came before it.",

    materialStrategy:
      "The material strategy here is protection, preservation, and final verification. I want the shell, finish, hardware, and setup to leave in a state that reflects their full potential. The instrument should arrive ready to be trusted, not requiring apology or explanation.",

    standardFoundations: [
      "Final inspection is treated with full seriousness",
      "Nothing unresolved gets passed on to the player",
      "Packaging is part of the craftsmanship experience",
      "The final handoff should feel worthy of the build journey",
    ],

    craftsmanChoices: [
      "Verifying every functional and aesthetic detail one last time",
      "Protecting the instrument for transit without compromising presentation",
      "Making sure the drum arrives as a finished statement",
      "Closing the process in a way that reflects the brand’s values",
    ],

    voiceSummary:
      "This chapter does not reshape the shell’s sound the way earlier chapters do, but it absolutely shapes trust, perceived quality, and the dignity of the final experience. It is the chapter that tells the player whether every earlier promise was truly meant.",

    voiceEffects: [
      "Trust at delivery",
      "Perceived completeness",
      "Brand integrity",
      "The emotional landing of the full build journey",
    ],

    voiceMantra:
      "A build is not finished when the last task is done. It is finished when nothing unworthy remains.",
  },
}